/**
 * Error Formatter Utility
 *
 * Provides generic error message extraction from various error object structures.
 * Handles error objects from different sources (API, file uploads, network, etc.)
 * and extracts human-readable messages instead of "[object Object]".
 */

import i18next from "i18next";

/**
 * Safely extracts a user-friendly error message from any error object
 *
 * @param error - Any error object (Error, API response, custom error, etc.)
 * @returns Human-readable error message string
 *
 * @example
 * ```typescript
 * try {
 *   await uploadFile(file);
 * } catch (error) {
 *   const message = formatErrorForDisplay(error);
 *   console.error("Operation failed:", message); // Not "[object Object]"
 * }
 * ```
 */
export function formatErrorForDisplay(error: any): string {
  const t = i18next.t;

  // Handle null/undefined
  if (!error) {
    return t("errors.unexpected", { defaultValue: "An unexpected error occurred" });
  }

  // If error is already a string, return it
  if (typeof error === "string") {
    return error;
  }

  // Try userMessage (from FileUploadError structure)
  if (error?.userMessage && typeof error.userMessage === "string") {
    return error.userMessage;
  }

  // Try standard Error.message
  if (error?.message && typeof error.message === "string") {
    // Avoid returning messages that are just object representations
    if (error.message !== "[object Object]") {
      return error.message;
    }
  }

  // Try detail.message (API error responses)
  if (error?.detail?.message && typeof error.detail.message === "string") {
    return error.detail.message;
  }

  // Try detail string (some APIs return detail as string)
  if (typeof error?.detail === "string") {
    return error.detail;
  }

  // Try response.data.message (Axios error structure)
  if (error?.response?.data?.message && typeof error.response.data.message === "string") {
    return error.response.data.message;
  }

  // Try response.data.detail.message (nested API error)
  if (
    error?.response?.data?.detail?.message &&
    typeof error.response.data.detail.message === "string"
  ) {
    return error.response.data.detail.message;
  }

  // Try response.statusText (HTTP errors)
  if (error?.response?.statusText && typeof error.response.statusText === "string") {
    return `${error.response.statusText}${error.response.status ? ` (${error.response.status})` : ""}`;
  }

  // Try status code mapping
  if (error?.status || error?.response?.status) {
    const status = error.status || error.response.status;

    const statusMessages: Record<number, { key: string; defaultValue: string }> = {
      400: { key: "errors.badRequest", defaultValue: "Bad request - please check your input" },
      401: { key: "errors.unauthorized", defaultValue: "Unauthorized - please log in" },
      403: { key: "errors.forbidden", defaultValue: "Forbidden - you don't have permission" },
      404: { key: "errors.notFound", defaultValue: "Resource not found" },
      408: { key: "errors.requestTimeout", defaultValue: "Request timeout" },
      413: { key: "errors.requestTooLarge", defaultValue: "Request too large" },
      415: { key: "errors.unsupportedFileType", defaultValue: "Unsupported file type" },
      429: {
        key: "errors.tooManyRequests",
        defaultValue: "Too many requests - please try again later",
      },
      500: { key: "errors.serverError", defaultValue: "Server error - please try again" },
      502: { key: "errors.badGateway", defaultValue: "Bad gateway - server is unavailable" },
      503: {
        key: "errors.serviceUnavailable",
        defaultValue: "Service unavailable - please try again later",
      },
      504: {
        key: "errors.gatewayTimeout",
        defaultValue: "Gateway timeout - server took too long to respond",
      },
    };

    const statusMessage = statusMessages[status];

    if (statusMessage) {
      return t(statusMessage.key, { defaultValue: statusMessage.defaultValue });
    }

    return t("errors.requestFailedWithStatus", {
      defaultValue: `Request failed with status ${status}`,
      status,
    });
  }

  // Try error code
  if (error?.code && typeof error.code === "string") {
    // Network errors
    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      return t("errors.connectionTimeout", {
        defaultValue: "Connection timeout - please try again",
      });
    }

    if (error.code === "ENOTFOUND" || error.code === "ECONNREFUSED") {
      return t("errors.unableToConnect", { defaultValue: "Unable to connect to server" });
    }

    if (error.code === "ERR_NETWORK") {
      return t("errors.networkError", {
        defaultValue: "Network error - please check your connection",
      });
    }

    return t("errors.errorWithCode", { defaultValue: `Error: ${error.code}`, code: error.code });
  }

  // Try name property (standard Error objects)
  if (error?.name && typeof error.name === "string" && error.name !== "Error") {
    return error.name;
  }

  // Fallback
  return t("errors.unexpected", { defaultValue: "An unexpected error occurred" });
}

/**
 * Formats error for console logging with full context
 *
 * @param error - Any error object
 * @param context - Optional context string (e.g., "File upload", "API call")
 * @returns Formatted log object with human-readable message and full error details
 *
 * @example
 * ```typescript
 * catch (error) {
 *   console.error(formatErrorForLogging(error, "File upload"));
 * }
 * ```
 */
export function formatErrorForLogging(
  error: any,
  context?: string,
): { context?: string; message: string; error: any } {
  return {
    ...(context && { context }),
    message: formatErrorForDisplay(error),
    error, // Include full error object for debugging
  };
}
